import _ from 'lodash';
import moment, { Moment } from 'moment';
import CurrentInterestFineImp from '@interfaces/calculations/CurrentInterestFineImp';
import CurrentAuthorImp from '@interfaces/calculations/CurrentAuthorImp';
import {
  CurrentAuthorTypes,
  InterestFineTypes,
  initialInterest,
  initialFine,
  typeFine,
  typeInterest,
  typeInterestCorrection,
  OccurrenceTypes,
  typeInterestCorrectionSuspend,
  typeFineCorrection,
  typeinstallment,
  typePayment,
} from '@/hooks/interfaces/CurrentAccountHookImp';
import AccountImp from '@interfaces/AccountImp';
import MemCalcImp from '@interfaces/MemCalcImp';
import CalculateInterest, { CalculateInterestImp } from '../../../InterestService';
import MonetaryInterestImp from '@interfaces/calculations/MonetaryInterestImp';
import {
  InterestTypes,
  InterestTypesWithPeriodAndFine,
  typeCompensatory,
  typeCompound,
  typeDefault,
  typePeriod,
} from '@data/calculations/interestTypes';
import { getCoin, roundNumber } from '../../../../utils/numberUtils';
import { PercentageOrFixedType, fineAmountType, finePercentageType } from '@data/calculations/fineEntryTypes';
import MonetaryCorrectionService from '../../../MonetaryCorrectionService';
import CorrectionImp, { MonetaryCorrectionCalculateImp } from '@interfaces/calculations/CorrectionImp';
import { CurrentTypes, typeAccountCurrent } from '@/data/calculations/currentTypes';
import ViewOccorrenceImp, { SummaryImp } from '@interfaces/calculations/ViewOccorrenceImp';
import MonetaryFineImp, { PeriodImp } from '@/interfaces/calculations/MonetaryFineImp';
import { valueWithCurrency } from '@lib/currency';
import { dateFormatEnum } from '@/enums/DateFormatEnum';
import { reorderItemsByIndex } from '@/lib/utils';
import { formatPeriodPercentage } from '@services/InterestService/CalculateSimpleInterest';
import { frequencyDayDaily } from '@/data/calculations/frequencyDaysTypes';
import TransitiveInterestService from '@/services/TransitiveInterestService';
import InterestIndexesImp from '@/interfaces/calculations/InterestIndexesImp';
import { getCapitalization, getPeriodicity } from '@/utils/getTimeByRange';
import { capitalizeFirstLetter } from '@/utils/capitalizeFirstLetter';
import {
  typeMonthlyBeforeLaw,
  typeNotApply,
  typeOnePercent,
  typeProRataDayBeforeLaw,
} from '@/data/calculations/civilCodeTypes';
import { doConversion } from '@/utils/conversionHelper';
import { labelsEnum } from '@/enums/labelsEnum';
import LawImp from '@/interfaces/Law';
import CivilCodeInterest from '@/enums/CivilCodeInterest';
import { getFieldName } from '@/lib/nomenclature';
import INomenclature from '@/interfaces/NomenclatureImp';

export type CreateResponseImp = CurrentAuthorImp[];

export interface CreateImp {
  authorList: CurrentAuthorImp[];
  authorIndex: number;
  updateTo: string;
  type: InterestFineTypes;
}

export type DeleteResponseImp = CurrentAuthorImp[];

export interface DeleteImp {
  authorList: CurrentAuthorImp[];
  authorIndex: number;
  interestFineIndex: number;
}

export type DuplicateResponseImp = CurrentAuthorImp[];

export interface CalculateImp {
  balance: number;
  installmentValue?: number;
  interestTotal: number;
  fineTotal: number;
  interestFineApplyByDate: Map<string, Array<string>>;
  views: ViewOccorrenceImp[];
  account: AccountImp;
  interestFineList: CurrentInterestFineImp[];
  summary: SummaryImp[];
  occurrence: {
    currentDate: Moment;
    nextDate: Moment;
    value: number;
    type: OccurrenceTypes;
  };
  type: CurrentTypes;
  isFirstOccurrence: boolean;
  isLastOccurrence: boolean;
  allMemcalcs: {
    [key: number]: MemCalcImp[];
  };
  isTest?: boolean;
  memCalcs: MemCalcImp[];
  interestIndexes: InterestIndexesImp;
  interestIndexesFromLaw: LawImp[];
  nomenclatures: INomenclature[];
}

export interface DuplicateImp {
  authorList: CurrentAuthorImp[];
  authorIndex: number;
  interestFineIndex: number;
}

export interface DateStartImp {
  date: string | null;
  currentOccurrenceDate: Moment;
  updateTo: string;
  isDaily?: boolean;
  isFirstOccurrence?: boolean;
}

export interface DateEndImp {
  date: string | null;
  nextOccurrenceDate: Moment;
  updateTo: string;
  isDaily?: boolean;
  isLastOccurrence?: boolean;
}

export default class InterestsFineService {
  protected views: ViewOccorrenceImp[] = [];
  protected interestFineApplyByDate: Map<string, Array<string>> = new Map();
  protected interestFineCorrectionApplyByDate: Map<string, Array<string>> = new Map();
  protected balance = 0;
  protected fineTotal = 0;
  protected interestTotal = 0;
  protected defaultTotal = 0;
  protected compensatoryTotal = 0;
  protected compoundTotal = 0;
  protected appliedTo = 0;
  protected calculateInterest = new CalculateInterest();
  protected monetaryCorrectionService = new MonetaryCorrectionService();

  constructor(views?: ViewOccorrenceImp[]) {
    if (views) this.views = views;
  }

  public definePriorities(interestFines: CurrentInterestFineImp[]) {
    function compareDates(interestFine1: CurrentInterestFineImp, interestFine2: CurrentInterestFineImp) {
      const dataA = moment(interestFine1.dateStart, dateFormatEnum.DEFAULT);
      const dataB = moment(interestFine2.dateStart, dateFormatEnum.DEFAULT);

      if (dataA.isSame(dataB)) return 0;
      if (dataA.isBefore(dataB)) return -1;

      return 1;
    }

    return interestFines.sort(compareDates);
  }

  private getDateStart = ({
    currentOccurrenceDate,
    date,
    updateTo,
    isDaily = false,
    isFirstOccurrence,
  }: DateStartImp) => {
    const interestFineDateStart = moment(date || updateTo, dateFormatEnum.DEFAULT);

    if (interestFineDateStart.isSameOrAfter(currentOccurrenceDate) || isFirstOccurrence)
      return interestFineDateStart.format(isDaily ? dateFormatEnum.DEFAULT : dateFormatEnum.ONE_DAY);
    return currentOccurrenceDate.format(isDaily ? dateFormatEnum.DEFAULT : dateFormatEnum.ONE_DAY);
  };

  private getDateEnd = ({ date, nextOccurrenceDate, updateTo, isDaily, isLastOccurrence }: DateEndImp) => {
    const dateEndDate = moment(date || updateTo, dateFormatEnum.DEFAULT);
    const updateToDate = moment(updateTo, dateFormatEnum.DEFAULT);

    if (dateEndDate.isSameOrAfter(updateToDate) && isLastOccurrence)
      return updateToDate.format(isDaily ? dateFormatEnum.DEFAULT : dateFormatEnum.ONE_DAY);

    if (dateEndDate.isSameOrBefore(nextOccurrenceDate) || isLastOccurrence)
      return dateEndDate.format(isDaily ? dateFormatEnum.DEFAULT : dateFormatEnum.ONE_DAY);
    else return nextOccurrenceDate.format(isDaily ? dateFormatEnum.DEFAULT : dateFormatEnum.ONE_DAY);
  };

  public orderByDate({
    author,
    authorIndex,
    endIndex,
    startIndex,
  }: {
    author: CurrentAuthorTypes;
    authorIndex: number;
    startIndex: number;
    endIndex: number;
  }) {
    if (!author.list.length) throw 'not found author list';

    const currentAuthor = _.cloneDeep(author) as CurrentAuthorTypes;
    const authorList = currentAuthor.list[authorIndex];
    const interestFines = authorList.interestFines;

    const initialOccurrence = interestFines[startIndex];
    const finalOccurrence = interestFines[endIndex];

    const initialDate = moment(initialOccurrence.dateStart, dateFormatEnum.DEFAULT);
    const finalDate = moment(finalOccurrence.dateStart, dateFormatEnum.DEFAULT);

    if (initialDate.isSame(finalDate)) {
      const reorderedOccurrences: CurrentInterestFineImp[] = reorderItemsByIndex(interestFines, startIndex, endIndex);
      authorList.interestFines = reorderedOccurrences;
    }

    return currentAuthor;
  }

  public order(
    interestFines: CurrentInterestFineImp[],
    currentOccurrenceDate: Moment,
    nextOccurrenceDate: Moment,
    changeUpdateTo?: boolean,
    lastUpdateTo?: string,
    updateTo?: string,
    changeInstallmentDate?: boolean,
    lastInstallmentDate?: string,
    occurrenceDate?: string,
    isDaily?: boolean
  ) {
    const format = isDaily ? dateFormatEnum.DEFAULT : dateFormatEnum.ONE_DAY;
    const isSameDate = currentOccurrenceDate.format(format) == nextOccurrenceDate.format(format);

    const interestFinesFiltered = interestFines.filter(interestFine => {
      const hasChangeDateEnd =
        changeUpdateTo &&
        moment(interestFine.dateEnd, dateFormatEnum.DEFAULT).isSame(moment(lastUpdateTo, dateFormatEnum.DEFAULT));

      if (hasChangeDateEnd) interestFine.dateEnd = updateTo || interestFine.dateEnd;

      const dateStart = moment(interestFine.dateStart, dateFormatEnum.DEFAULT);

      const hasChangeDateStart =
        changeInstallmentDate && dateStart.isSame(moment(lastInstallmentDate, dateFormatEnum.DEFAULT));

      if (hasChangeDateStart) interestFine.dateStart = occurrenceDate || interestFine.dateStart;

      const dateEnd = moment(interestFine.dateEnd, dateFormatEnum.DEFAULT);

      const typePeriodFine = interestFine.type.includes(typeFine.id) && interestFine.periodicity.includes('period');

      const isFineSameDate = dateStart.isSame(dateEnd);

      const updateToMoment = moment(updateTo, dateFormatEnum.DEFAULT);

      console.log(updateTo);
      const isBeforeOrAtEndDate =
        dateEnd.isBefore(nextOccurrenceDate) || (dateEnd.isSame(updateToMoment) && nextOccurrenceDate.isSame(updateTo));

      if (typePeriodFine && isFineSameDate) {
        console.log(
          dateEnd.format(),
          dateStart.format(),
          interestFine.type,
          currentOccurrenceDate.format(),
          nextOccurrenceDate.format(),
          dateEnd.isSame(updateTo) && nextOccurrenceDate.isSame(updateTo),
          dateEnd.isBefore(nextOccurrenceDate),
          dateStart.isSameOrAfter(currentOccurrenceDate),
          dateStart.isSameOrAfter(currentOccurrenceDate) &&
            (dateEnd.isBefore(nextOccurrenceDate) || (dateEnd.isSame(updateTo) && nextOccurrenceDate.isSame(updateTo)))
        );
        const occurrenceIsBetweenToInterestFine = dateStart.isSameOrAfter(currentOccurrenceDate) && isBeforeOrAtEndDate;

        return occurrenceIsBetweenToInterestFine;
      }

      const dateEndIsBetweenToOccurrence = dateEnd.isSameOrAfter(currentOccurrenceDate) && isBeforeOrAtEndDate;

      const dateStartIsBetweenToOccurrence =
        dateStart.isSameOrAfter(currentOccurrenceDate) && dateStart.isBefore(nextOccurrenceDate);

      const currentOccurrenceIsBetweenToInterestFine =
        currentOccurrenceDate.isSameOrAfter(dateStart) && currentOccurrenceDate.isBefore(dateEnd);

      return (
        !isSameDate &&
        (dateEndIsBetweenToOccurrence || dateStartIsBetweenToOccurrence || currentOccurrenceIsBetweenToInterestFine)
      );
    });

    return this.definePriorities(interestFinesFiltered);
  }
  public orderAuthors(author: CurrentAuthorTypes) {
    const newAuthor: CurrentAuthorTypes = _.cloneDeep(author);
    const newAuthorList = author.list.map(author => {
      const interestFineList = this.definePriorities(author.interestFines);
      return { ...author, interestFines: interestFineList };
    });

    newAuthor.list = newAuthorList;

    return newAuthor;
  }

  private getInterestTotalByType = (payloadView: ViewOccorrenceImp, description: InterestTypes) => {
    switch (description) {
      case typeDefault.id:
        this.defaultTotal += payloadView.value;
        payloadView.defaultTotal = this.defaultTotal;
        break;
      case typeCompensatory.id:
        this.compensatoryTotal += payloadView.value;
        payloadView.compensatoryTotal = this.compensatoryTotal;
        break;
      case typeCompound.id:
        this.compoundTotal += payloadView.value;
        payloadView.compoundTotal = this.compoundTotal;
        break;
    }

    this.views.push(payloadView);
  };

  private generateTransitiveInterestDescription({
    interestFine,
    isFine,
    isLawType,
    result,
    periods,
    view,
  }: {
    interestFine: any;
    isFine: boolean;
    isLawType: boolean;
    result: number;
    periods?: PeriodImp[];
    view: ViewOccorrenceImp;
  }) {
    const getFirstDesc = (period: PeriodImp) => {
      const fromIsBeforeLaw = moment(period.from, dateFormatEnum.DEFAULT).isBefore(
        moment('30/08/2024', dateFormatEnum.DEFAULT)
      );

      const percentage = `${formatPeriodPercentage(interestFine.value || 0)}%`;
      const periodicity =
        interestFine.civilCode == typeOnePercent.id || (isLawType && fromIsBeforeLaw)
          ? getPeriodicity(interestFine.periodicity)
          : '';

      if (isLawType && fromIsBeforeLaw)
        return `${percentage} ${periodicity} (de ${period.from} a ${period.to}) = ${period.percentage}% \n`;

      return `(${capitalizeFirstLetter(period.nomenclature)} ${periodicity} de ${period.from} a ${period.to}) = ${
        period.percentage
      }% \n`;
    };

    const getSecoundDesc = (period: PeriodImp) => {
      const currencyEnd = getCoin(interestFine.dateEnd, 0);
      const currency = getCoin(period.to, 0);
      const appliedTo = valueWithCurrency(currency, this.appliedTo);
      const resultInterest = valueWithCurrency(currencyEnd, this.interestTotal + result);
      const resultFine = valueWithCurrency(currencyEnd, this.balance + result);
      const nomenclature = capitalizeFirstLetter(period.nomenclature);
      return `(${nomenclature} = ${period.percentage}%) sobre ${appliedTo} = ${valueWithCurrency(currency, result)} ${
        !isFine
          ? `\nSomado ao juros anterior ${valueWithCurrency(view.currency, view.interestBalance)}`
          : `\nSomado ao principal anterior ${valueWithCurrency(view.currency, view.balance)}`
      } = ${isFine ? resultFine : resultInterest} \n`;
    };

    let description = '';

    periods?.forEach((period, index) => {
      if (index != periods?.length - 1) description += getFirstDesc(period);
      else description += getSecoundDesc(period);
    });

    return description;
  }

  private generatePercentageFineDescription = ({
    interestFine,
    result: fineResult,
    totalPercentage,
    view,
  }: {
    interestFine: CurrentInterestFineImp;
    totalPercentage: number;
    result: number;
    view: ViewOccorrenceImp;
  }) => {
    const currencyEnd = getCoin(interestFine.dateEnd, 0);
    const percentage = `${formatPeriodPercentage(interestFine.value || 0)}%`;
    const periodicity = getPeriodicity(interestFine.periodicity);
    const capitalization =
      interestFine.type == typeInterest.id && interestFine.description == typeCompound.id
        ? `${getCapitalization(interestFine.capitalization)}`
        : '';
    const percentageTotal = `${formatPeriodPercentage(totalPercentage || 0)}%`;

    const result = valueWithCurrency(currencyEnd, (view.fineBalance || 0) + fineResult);

    const detail = `\nSomado ao saldo da multa anterior ${valueWithCurrency(
      view.currency,
      view.fineBalance || 0
    )} = ${result}`;

    const appliedToBalanceFormated = valueWithCurrency(currencyEnd, fineResult);
    const appliedTo = valueWithCurrency(view.currencyBalance || view.currency, this.appliedTo);

    return `${percentage} ${periodicity} ${capitalization} = ${percentageTotal} sobre ${appliedTo} = ${appliedToBalanceFormated}\n ${detail}`;
  };

  private generateAmountFineDescription = ({
    interestFine,
    result: interestResult,
    totalValue,
    view,
  }: {
    interestFine: any;
    totalValue: number;
    result: number;
    view: ViewOccorrenceImp;
  }) => {
    const currencyStart = getCoin(interestFine.dateStart, 0);
    const currencyEnd = getCoin(interestFine.dateEnd, 0);
    const value = valueWithCurrency(currencyStart, interestFine.value || 0);
    const periodicity = getPeriodicity(interestFine.periodicity);
    const valueTotal = valueWithCurrency(currencyEnd, totalValue || 0);
    const result = valueWithCurrency(currencyEnd, this.balance + interestResult);
    const detail = `\nSomado ao principal anterior  ${valueWithCurrency(view.currency, view.balance)} = ${result}`;

    return `(${value} ${periodicity} de ${interestFine.dateStart} a ${interestFine.dateEnd}) = ${valueTotal} ${detail}\n`;
  };

  private generatePercentageInterestDescription = ({
    interestFine,
    result: interestResult,
    totalPercentage,
    isInterestType,
    view,
  }: {
    interestFine: any;
    isInterestType: boolean;
    totalPercentage: number;
    result: number;
    view: ViewOccorrenceImp;
  }) => {
    if (!isInterestType) return '';

    const currencyEnd = getCoin(interestFine.dateEnd, 0);
    const percentage = `${formatPeriodPercentage(interestFine.value || 0)}%`;
    const periodicity = getPeriodicity(interestFine.periodicity);
    const capitalization =
      interestFine.description == typeCompound.id ? `${getCapitalization(interestFine.capitalization)}` : '';
    const percentageTotal = `${formatPeriodPercentage(totalPercentage || 0)}%`;
    const result = valueWithCurrency(currencyEnd, this.interestTotal + interestResult);
    const detail = `\nSomado ao saldo do juros anterior ${valueWithCurrency(
      view.currency,
      view.interestBalance
    )} = ${result}`;

    const appliedTo = valueWithCurrency(view.currencyBalance || view.currency, this.appliedTo);
    const appliedToBalanceFormated = valueWithCurrency(currencyEnd, interestResult);

    return `${percentage} ${periodicity} ${capitalization} = ${percentageTotal} sobre ${appliedTo} = ${appliedToBalanceFormated} ${detail}`;
  };

  public generateDescription = ({
    interestFine,
    result,
    totalPercentage,
    periods,
    totalValue,
    appliedTo,
  }: {
    interestFine: CurrentInterestFineImp;
    totalPercentage: number;
    totalValue: number;
    result: number;
    periods: PeriodImp[];
    appliedTo?: number;
  }) => {
    const isInterestType = interestFine.type == typeInterest.id;
    const type: PercentageOrFixedType | InterestTypes = isInterestType
      ? (interestFine.description as InterestTypes)
      : interestFine.selectType;
    const isFineType = interestFine.type == typeFine.id;
    const isFineAmountType = type == fineAmountType.id && isFineType;
    const isFinePercentageType = type == finePercentageType.id && isFineType;
    const lawTypes = [typeMonthlyBeforeLaw.id, typeProRataDayBeforeLaw.id];
    const isLawType = lawTypes.includes(interestFine.civilCode as CivilCodeInterest);

    const lastView = this.views[this.views.length - 1];
    const isApply = interestFine.civilCode != typeNotApply.id;

    if (isApply) {
      if (isInterestType)
        return this.generateTransitiveInterestDescription({
          interestFine,
          isFine: false,
          isLawType,
          periods,
          result,
          view: lastView,
        });

      return this.generateTransitiveInterestDescription({
        interestFine,
        isFine: true,
        isLawType,
        periods,
        result,
        view: lastView,
      });
    }

    if (isFinePercentageType) {
      this.appliedTo = appliedTo || lastView.balance || 0;
      return this.generatePercentageFineDescription({
        interestFine,
        totalPercentage,
        result,
        view: lastView,
      });
    }

    if (isFineAmountType)
      return this.generateAmountFineDescription({
        interestFine,
        totalValue,
        result,
        view: lastView,
      });

    this.appliedTo = appliedTo || lastView.balance || 0;
    return this.generatePercentageInterestDescription({
      interestFine,
      totalPercentage,
      isInterestType,
      result,
      view: lastView,
    });
  };

  public async calculate({
    interestFineApplyByDate,
    account,
    balance,
    views,
    allMemcalcs,
    summary,
    interestFineList,
    interestTotal,
    type: currentAccountType,
    occurrence: { value: occurrenceValue, currentDate, nextDate, type: occurrenceType },
    isFirstOccurrence,
    isLastOccurrence,
    memCalcs,
    isTest,
    interestIndexes,
    interestIndexesFromLaw,
    nomenclatures,
    fineTotal,
    installmentValue,
  }: CalculateImp) {
    console.info(`calculate_interest_and_fine_${currentAccountType}`);

    const transitiveInterestService = new TransitiveInterestService();

    this.interestFineApplyByDate = interestFineApplyByDate;
    this.views = views;
    this.balance = balance;
    this.fineTotal = fineTotal;
    this.interestTotal = interestTotal;

    for (let interestFineIndex = 0; interestFineIndex < interestFineList.length; interestFineIndex++) {
      const interestFine = interestFineList[interestFineIndex];

      // Identifica se o item atual tem correção ativa
      const currentIsCorrection = interestFine.isCorrection;
      let isLastFineCorrectionInPeriod = false;

      // Variável para determinar se é a última correção do período

      if (currentIsCorrection) {
        // "Olha para a frente" no array para ver se existe alguma OUTRA multa
        // com correção dentro do mesmo intervalo de data (currentDate até nextDate)
        const hasNextFineCorrectionInPeriod = interestFineList.slice(interestFineIndex + 1).some(nextinterestFine => {
          const isSamePeriod =
            moment(nextinterestFine.dateStart, dateFormatEnum.DEFAULT).isSameOrAfter(currentDate) &&
            moment(nextinterestFine.dateEnd, dateFormatEnum.DEFAULT).isSameOrBefore(nextDate);

          return nextinterestFine.isCorrection && isSamePeriod && nextinterestFine.type.includes(typeFine.id);
        });

        // Se NÃO houver uma próxima correção no mesmo período, significa que ESTE atual é o último!
        isLastFineCorrectionInPeriod = !hasNextFineCorrectionInPeriod;
      } else {
        isLastFineCorrectionInPeriod = false;
      }

      if (!interestFine.civilCode?.length) interestFine.civilCode = typeNotApply.id;
      const newInterest = initialInterest;

      const isInterestType = interestFine.type == typeInterest.id;
      const isFineType = interestFine.type == typeFine.id;
      const type: PercentageOrFixedType | InterestTypes = isInterestType
        ? (interestFine.description as InterestTypes)
        : interestFine.selectType;

      const isCorrection = interestFine.isCorrection;

      const isDayPeriodicity =
        interestFine.periodicity == frequencyDayDaily.value ||
        account.proRataDay ||
        [CivilCodeInterest.MONTHLY_BEFORE_LAW, CivilCodeInterest.PRO_RATA_DAY_BEFORE_LAW].includes(
          interestFine.civilCode
        );

      const typeFineIsPeriod = interestFine.type == typeFine.id && interestFine.periodicity.includes(typePeriod.id);
      const typeInterestIsPeriod =
        interestFine.type == typeInterest.id && interestFine.periodicity.includes(typePeriod.id);

      const dateStart = this.getDateStart({
        date: interestFine.dateStart,
        currentOccurrenceDate: currentDate,
        updateTo: account.updateTo,
        isDaily: isDayPeriodicity,
        isFirstOccurrence,
      });

      let dateEnd = this.getDateEnd({
        isDaily: isDayPeriodicity,
        date: interestFine.dateEnd,
        nextOccurrenceDate: nextDate,
        updateTo: account.updateTo,
        isLastOccurrence,
      });

      let appliedToBalance = 0;

      const calculatedInfo: CalculateInterestImp = {
        ...newInterest.calculatedInfo,
        value: newInterest.type.includes(typeFine.id) ? installmentValue || 0 : this.balance,
      };

      this.pushSummary(summary, interestFine, dateStart, dateEnd, account.proRataDay);

      const payload = this.buildPayload({
        newInterest,
        type,
        dateStart,
        dateEnd,
        interestFine,
        calculatedInfo,
      });

      let notApply = false;
      const transitive = interestIndexes
        ? await transitiveInterestService.run({
            interest: payload,
            interestIndexes,
            allMemcalcs,
            interestIndexesFromLaw,
            proRataDay: account.proRataDay,
          })
        : undefined;

      if (transitive) payload.percentage = 1;

      const defaultInstallmentInterest: CalculateInterestImp = this.calculateInterest.run(
        payload,
        transitive,
        account.proRataDay
      );

      if (
        moment(dateStart, dateFormatEnum.DEFAULT).isAfter(moment(dateEnd, dateFormatEnum.DEFAULT)) ||
        moment(dateEnd, dateFormatEnum.DEFAULT).isBefore(moment(dateStart, dateFormatEnum.DEFAULT))
      )
        notApply = true;

      const typesAppied = this.interestFineApplyByDate.get(`${dateStart}_${dateEnd}`) || [];
      notApply = notApply && typesAppied.includes(interestFine.type);

      if (!notApply) {
        appliedToBalance = this.executeApplication({
          interestFine,
          type,
          installmentValue,
          defaultInstallmentInterest,
        });

        this.interestFineApplyByDate.delete(`${dateStart}_${dateEnd}`);
        this.interestFineApplyByDate.set(`${dateStart}_${dateEnd}`, typesAppied.concat(interestFine.type));
      }

      if ((typeFineIsPeriod || typeInterestIsPeriod) && isCorrection) {
        dateEnd = nextDate.format(dateFormatEnum.DEFAULT);
      }

      if (!notApply) {
        const descriptionParams = {
          interestFine,
          dateStart,
          dateEnd,
          transitive,
          result: appliedToBalance,
          defaultInstallmentInterest,
          installmentValue,
          isFineType,
        };

        if (currentAccountType === typeAccountCurrent.id) {
          this.processCurrentAccount({
            ...descriptionParams,
            occurrenceType,
          });
        } else {
          await this.processDefaultAccount({
            ...descriptionParams,
            account,
            dateStart,
            dateEnd,
            interestFine,
            memCalcs,
            isTest,
            isInterestType,
            isFineType,
            nomenclatures,
            isCorrection,
            occurrenceType,
            payload,
            appliedToBalance,
            currentDate,
            nextDate,
            isLastFineCorrectionInPeriod,
          });
        }

        this.interestFineApplyByDate.delete(`${dateStart}_${dateEnd}`);
        this.interestFineApplyByDate.set(`${dateStart}_${dateEnd}`, typesAppied.concat(interestFine.type));
      }
    }

    return {
      views: this.views,
      balanceTotal: this.balance,
      interestTotal: this.interestTotal,
      total: this.balance + this.interestTotal,
      fineTotal: this.fineTotal,
      defaultTotal: this.defaultTotal,
      compensatoryTotal: this.compensatoryTotal,
      compoundTotal: this.compoundTotal,
      interestFineApplyByDate,
    };
  }

  // ==========================================
  // MÉTODOS AUXILIARES EXTRAÍDOS (PRIVADOS)
  // ==========================================

  private pushSummary(
    summary: any[],
    interestFine: any,
    dateStart: string,
    dateEnd: string,
    proRataDay: boolean
  ): void {
    const diffMonth = (
      !proRataDay
        ? moment(interestFine.dateEnd, dateFormatEnum.DEFAULT).add(1, 'month')
        : moment(interestFine.dateEnd, dateFormatEnum.DEFAULT)
    ).diff(moment(dateStart, dateFormatEnum.DEFAULT), 'month');

    const year = Number((diffMonth / 12).toFixed(2));
    summary.push({ date: dateEnd, desc: typeInterest.label, from: typeInterest.id, year });
  }

  private buildPayload({ newInterest, type, dateStart, dateEnd, interestFine, calculatedInfo }: any): any {
    return {
      ...newInterest,
      type,
      dateEnd,
      dateStart,
      formula: interestFine.formula,
      index: interestFine.index,
      calculatedInfo,
      percentage: interestFine.value,
      capitalization: interestFine.type == typeInterest.id ? interestFine.capitalization : 'none',
      periodicity: interestFine.periodicity,
      poupancaType: interestFine.type == typeInterest.id ? interestFine.poupancaType : undefined,
      civilCode: interestFine.civilCode,
      civilCodeDate: interestFine.civilCodeDate,
      administrativeNatureFirstDate:
        interestFine.type == typeInterest.id ? interestFine.administrativeNatureFirstDate : undefined,
      administrativeNatureSecondDate:
        interestFine.type == typeInterest.id ? interestFine.administrativeNatureSecondDate : undefined,
      administrativeNatureThirdDate:
        interestFine.type == typeInterest.id ? interestFine.administrativeNatureThirdDate : undefined,
      onCompensatoryInterest: interestFine.onCompensatoryInterest,
      onInterestWithoutCorrection: interestFine.type == typeInterest.id && interestFine.onInterestWithoutCorrection,
      onCompoundInterest: interestFine.onCompoundInterest,
      onDefaultInterest: interestFine.onDefaultInterest,
      onInstallmentsValue: interestFine.onInstallmentsValue,
      onInterestPeriod: interestFine.onInterestPeriod,
    };
  }

  private executeApplication({ interestFine, type, installmentValue, defaultInstallmentInterest }: any): number {
    let appliedToBalance = 0;
    const applyByPercentage = (value: number) => {
      this.appliedTo = value;
      return value * (defaultInstallmentInterest.totalPercentage / 100);
    };
    const applyByAmount = () => defaultInstallmentInterest.result;

    switch (interestFine.type) {
      case typeInterest.id: {
        appliedToBalance = applyByPercentage(this.balance);
        break;
      }
      case typeFine.id: {
        switch (type) {
          case fineAmountType.id:
            appliedToBalance = applyByAmount();
            break;
          case finePercentageType.id:
            appliedToBalance = applyByPercentage(installmentValue || 0);
            break;
        }
        break;
      }
    }
    return appliedToBalance;
  }

  private processCurrentAccount({
    interestFine,
    dateStart,
    dateEnd,
    transitive,
    result,
    defaultInstallmentInterest,
    installmentValue,
    isFineType,
    occurrenceType,
  }: any): void {
    this.balance += result;
    const currentDescription = this.generateDescription({
      interestFine: { ...interestFine, dateEnd, dateStart },
      periods: transitive?.periods || [],
      result,
      totalPercentage: defaultInstallmentInterest.totalPercentage,
      totalValue: defaultInstallmentInterest.result,
      appliedTo: isFineType ? installmentValue : undefined,
    });

    const currency = getCoin(dateEnd, 0);
    const payloadView: ViewOccorrenceImp = {
      updateSince: null,
      balance: this.balance,
      date: dateStart,
      dateEnd: dateEnd,
      description: interestFine.description,
      currency,
      tax: null,
      type: interestFine.type,
      selectType: interestFine.description as InterestTypesWithPeriodAndFine,
      isCorrection: interestFine.isCorrection,
      extraDescription: currentDescription,
      interestBalance: this.interestTotal,
      total: this.balance + this.fineTotal + this.interestTotal,
      fineBalance: this.fineTotal,
      value: result,
      correctedFrom: occurrenceType,
    };

    payloadView.value && this.getInterestTotalByType(payloadView, interestFine.description as InterestTypes);
    this.balance += this.interestTotal;
  }

  private async processDefaultAccount({
    interestFine,
    dateStart,
    dateEnd,
    transitive,
    result,
    defaultInstallmentInterest,
    installmentValue,
    isFineType,
    account,
    memCalcs,
    isTest,
    isInterestType,
    nomenclatures,
    isCorrection,
    occurrenceType,
    payload,
    appliedToBalance,
    currentDate,
    nextDate,
    isLastFineCorrectionInPeriod,
  }: any): Promise<void> {
    this.balance = roundNumber(this.balance);
    const dateEndMoment = moment(payload.dateEnd, dateFormatEnum.DEFAULT);
    const dateStartMoment = moment(payload.dateStart, dateFormatEnum.DEFAULT);
    const dateStartCorrection = dateEndMoment.isBefore(currentDate)
      ? currentDate.format(dateFormatEnum.DEFAULT)
      : payload.dateStart;

    const _currentDescription = this.generateDescription({
      interestFine: { ...interestFine, dateEnd, dateStart },
      periods: transitive?.periods || [],
      result,
      totalPercentage: defaultInstallmentInterest.totalPercentage,
      totalValue: defaultInstallmentInterest.result,
      appliedTo: isFineType ? installmentValue : undefined,
    });

    const processInterestFine = () => {
      if (isFineType) this.fineTotal += result;
      if (isInterestType) this.interestTotal += result;

      const payloadView: ViewOccorrenceImp = {
        updateSince: null,
        balance: this.balance,
        date: payload.dateStart,
        dateEnd: payload.dateEnd,
        description: interestFine.description,
        extraDescription: _currentDescription,
        tax: null,
        currency: getCoin(dateEnd, 0),
        type: interestFine.type,
        selectType: interestFine.description as InterestTypesWithPeriodAndFine,
        isCorrection,
        value: result,
        interestBalance: this.interestTotal,
        fineBalance: this.fineTotal,
        total: this.balance + this.interestTotal + this.fineTotal,
        correctedFrom: occurrenceType,
      };

      const isPaymentType = occurrenceType.includes(typePayment.id);
      const isShowPayloadView = payloadView.value && ((isFineType && !isPaymentType) || isInterestType);
      if (isShowPayloadView) this.getInterestTotalByType(payloadView, interestFine.description as InterestTypes);
    };

    const processCorrection = async () => {
      const typesAppied = this.interestFineCorrectionApplyByDate.get(`${dateStartCorrection}_${dateEnd}`) || [];

      if (typesAppied.includes(interestFine.type)) return;

      const interestBalanceCorrection = isInterestType ? this.interestTotal : this.fineTotal;

      const currentCorrectionPayload: MonetaryCorrectionCalculateImp = {
        account: { ...account, updateTo: dateEnd },
        date: dateStartCorrection,
        value: interestBalanceCorrection,
        memCalcs,
        isTest,
      };

      const correction = await this.monetaryCorrectionService.calculate(currentCorrectionPayload);
      const convertedValue = doConversion(currentCorrectionPayload.value, dateStart, dateEnd || account.updateTo);
      const correctionValue = correction.value ? correction.value - convertedValue : 0;

      if (!correctionValue) return;

      this.interestFineCorrectionApplyByDate.set(
        `${dateStartCorrection}_${dateEnd}`,
        typesAppied?.concat(interestFine.type)
      );

      if (isInterestType) {
        this.interestTotal = doConversion(this.interestTotal, dateStart, dateEnd || account.updateTo);
        this.interestTotal += correctionValue;
      }

      if (isFineType) {
        this.fineTotal = doConversion(this.fineTotal, dateStart, dateEnd || account.updateTo);
        this.fineTotal += correctionValue;
      }

      const currency = getCoin(dateEnd, 0);
      const generateDetail = () => {
        const currencyEnd = getCoin(dateEnd, 0);
        const lastView = this.views[this.views.length - 1];

        if (isInterestType) {
          return `\nSomado ao saldo do ${getFieldName(
            labelsEnum.INTEREST,
            nomenclatures
          )} anterior  ${valueWithCurrency(lastView.currency, lastView.interestBalance)} = ${valueWithCurrency(
            currencyEnd,
            this.interestTotal
          )}`;
        }

        return `\nSomado ao saldo da ${getFieldName(labelsEnum.FINE, nomenclatures)} anterior  ${valueWithCurrency(
          lastView.currency,
          lastView?.fineBalance || 0
        )} = ${valueWithCurrency(currencyEnd, this.fineTotal)}`;
      };

      const payloadView: ViewOccorrenceImp = {
        updateSince: null,
        balance: this.balance,
        date: dateStartCorrection,
        dateEnd,
        description: interestFine.description,
        extraDescription: ` ${labelsEnum.MONETARY_CORRECTION} ${
          isInterestType ? labelsEnum.APPLY_INTEREST_BALANCE : labelsEnum.APPLY_FINE_BALANCE
        } ${valueWithCurrency(currency, interestBalanceCorrection)} = ${valueWithCurrency(
          currency,
          correctionValue
        )} ${generateDetail()}`,
        tax: null,
        currency: getCoin(dateEnd, 0),
        type: isInterestType ? typeInterestCorrection.id : typeFineCorrection.id,
        value: correctionValue,
        isCorrection,
        fineBalance: this.fineTotal,
        interestBalance: this.interestTotal,
        total: this.balance + this.interestTotal + this.fineTotal,
        correctedFrom: occurrenceType,
      };

      (interestFine.type.includes(typeFine.id) ? this.fineTotal : this.interestTotal) &&
        correctionValue &&
        this.getInterestTotalByType(payloadView, interestFine.description as InterestTypes);

      const emptyCorrectionView: ViewOccorrenceImp = {
        updateSince: null,
        balance: this.balance,
        date: dateStart,
        dateEnd,
        description: '',
        extraDescription: `A correção não foi aplicada no período de ${dateStart} à ${dateEnd} devido a utilização do ${_currentDescription
          .split('=')[0]
          .trim()
          .replace(/^\(/, '')
          .toUpperCase()} (Sem Correção)`,
        tax: null,
        currency: getCoin(dateEnd, 0),
        type: typeInterestCorrectionSuspend.id,
        selectType: interestFine.description as InterestTypesWithPeriodAndFine,
        isCorrection,
        value: 0,
        interestBalance: 0,
        fineBalance: this.fineTotal,
        total: this.balance + this.interestTotal + this.fineTotal - appliedToBalance,
        correctedFrom: occurrenceType,
      };

      payload.onInterestWithoutCorrection &&
        this.getInterestTotalByType(emptyCorrectionView, interestFine.description as InterestTypes);
    };

    const lastBalance = isInterestType ? this.interestTotal : this.fineTotal;

    if (isCorrection && isInterestType) await processCorrection();

    console.log(interestFine.type, interestFine.dateStart, interestFine.dateEnd);
    processInterestFine();

    if (
      isCorrection &&
      (isFineType ? isLastFineCorrectionInPeriod : true) &&
      !(dateEndMoment.isSame(nextDate) && !lastBalance) &&
      dateEndMoment.isBefore(nextDate)
    )
      await processCorrection();
  }

  public errorType(type: number) {
    switch (type) {
      case 100:
        return 'occurrence not found';
      case 101:
        return 'smaller date not found';
      default:
        return '';
    }
  }

  public translateErrorType(type: string) {
    switch (type) {
      case 'occurrence not found':
        return 'Não existe ocorrência';
      case 'smaller date not found':
        return 'Menor data não encontrada';
      default:
        return '';
    }
  }

  public create({ authorList, authorIndex, type, updateTo }: CreateImp): CreateResponseImp {
    const newAuthorList = Array.from(authorList);
    const interestFines = newAuthorList[authorIndex].interestFines;
    const occurrences = authorList[authorIndex]?.occurrences.sort((occ, occ2) =>
      Number(moment(occ.date, dateFormatEnum.DEFAULT).isAfter(moment(occ2.date, dateFormatEnum.DEFAULT)))
    );

    let smallerDate = occurrences[0].date;
    if (!newAuthorList[authorIndex].occurrences.length) throw 100;
    if (!smallerDate) smallerDate = '01/01/2000';

    const interestFine: CurrentInterestFineImp =
      type == typeFine.id
        ? { ...initialFine, dateStart: smallerDate, dateEnd: updateTo, type }
        : { ...initialInterest, type, dateStart: smallerDate, dateEnd: updateTo };

    interestFines.unshift(_.cloneDeep(interestFine));
    newAuthorList[authorIndex].occurrenceTotal = 0;

    return newAuthorList;
  }

  public delete({ authorList, authorIndex, interestFineIndex }: DeleteImp): DeleteResponseImp {
    const newAuthorList = Array.from(authorList);
    newAuthorList[authorIndex].interestFines.splice(interestFineIndex, 1);
    newAuthorList[authorIndex].occurrenceTotal = 0;
    return newAuthorList;
  }

  public duplicate({ authorList, authorIndex, interestFineIndex }: DuplicateImp): DuplicateResponseImp {
    const newAuthorList = Array.from(authorList);
    const interestFine: CurrentInterestFineImp = newAuthorList[authorIndex].interestFines[interestFineIndex];
    newAuthorList[authorIndex].interestFines.push(_.assign({}, _.cloneDeep(interestFine)));
    newAuthorList[authorIndex].occurrenceTotal = 0;

    return newAuthorList;
  }
}
